/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */
import React, { useState } from 'react';
import { colors } from '@getgo/chameleon-core';
import { defineMessages, useIntl } from 'react-intl';
import {
  CloseIcon,
  Flex,
  IconButton,
  Image,
  Text,
} from '@getgo/chameleon-chakra-ui';

import { getAndroidAppUrl, getIosAppUrl } from '../../services/UrlService';
import { isAndroid, isIos, isSafari } from '../../services/ClientService';
import { Spacing } from '../../themes/GoToTheme';
import { GoToLink } from '../Link/GoToLink';
import { Product } from '../../services/IndexPageReaderService';

const messages = defineMessages({
  free: {
    id: 'mobileJoinPage.banner.free',
  },
  onTheAppStore: {
    id: 'mobileJoinPage.banner.appStore',
  },
  onThePlayStore: {
    id: 'mobileJoinPage.banner.playStore',
  },
  view: {
    id: 'mobileJoinPage.banner.view',
  },
  appName: {
    id: 'goto.mobile.banner.appName',
  },
  ariaClose: {
    id: 'goto.mobile.banner.ariaClose',
  },
});

interface Props {
  product: Product;
  showIf?: boolean;
}

export const GoToAppBanner: React.FC<Props> = ({
  product,
  showIf = isAndroid || (isIos && !isSafari),
}) => {
  const { formatMessage: f } = useIntl();
  const title = f(messages.appName);
  const author = 'LogMeIn, Inc.';
  const [isVisible, showBanner] = useState(showIf);

  const productToMobileIcon = {
    [Product.G2M]: `${process.env.PUBLIC_URL}/static/media/daisy-mobileG2M.svg`,
    [Product.G2T]: isAndroid
      ? `${process.env.PUBLIC_URL}/static/media/GoToTrainingAndroidDaisy.png`
      : `${process.env.PUBLIC_URL}/static/media/daisy-mobileG2TiOS.svg`,
    [Product.G2W]: `${process.env.PUBLIC_URL}/static/media/daisy-mobileG2W.svg`,
  };
  const mobileIcon = productToMobileIcon[product];

  if (!isVisible) {
    return null;
  }

  return (
    <Flex
      justifyContent='space-between'
      bg={colors.neutrals.n100}
      margin={1}
      paddingY={2}
      alignItems='center'
    >
      <Flex alignItems='center'>
        <IconButton
          aria-label={f(messages.ariaClose)}
          icon={
            <CloseIcon
              width={Spacing.CoreLayout1}
              height={Spacing.CoreLayout1}
              color={colors.neutrals.n600}
            />
          }
          onClick={() => showBanner(false)}
          bg='none'
        />
        <Image
          src={mobileIcon}
          alt={title}
          objectFit='cover'
          height='54px'
          mr='3'
        />
        <Flex direction='column' mb={1}>
          <Text fontSize='md' lineHeight='1.5' color={colors.neutrals.n600}>
            {title}
          </Text>
          <Text fontSize='sm' color={colors.neutrals.n500} lineHeight='1.1'>
            {author}
          </Text>
          <Text fontSize='sm' color={colors.neutrals.n500} lineHeight='1.1'>
            {f(messages.free)} -{' '}
            {isIos ? f(messages.onTheAppStore) : f(messages.onThePlayStore)}
          </Text>
        </Flex>
      </Flex>
      <GoToLink
        mr={5}
        isExternal
        href={isIos ? getIosAppUrl(product) : getAndroidAppUrl(product)}
      >
        {f(messages.view)}
      </GoToLink>
    </Flex>
  );
};
